import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { login, verify2fa, completePasswordReset } from '../services/authService';
import { hardRedirect } from '../services/api';
import { serverMessage } from '../services/errors';
import './Login.css';

const SUSPENDED_FALLBACK =
  'Your agency account is not active. Contact your agency administrator.';

/**
 * Where to land after a successful sign-in. An INITIAL_ADMIN has no agency bound
 * yet, so every tenant-scoped screen would fail until they choose one.
 */
const landingFor = (auth) => (auth?.tenantSelectionRequired ? '/select-agency' : '/');

/**
 * A suspended agency is not a credentials problem, and must not be reported as
 * one — the user would go round a password-reset loop that cannot help. Any other
 * failure stays deliberately vague rather than confirming which half was wrong.
 */
const messageFor = (err, generic) =>
  err?.response?.data?.code === 'TENANT_SUSPENDED'
    ? err.response.data.message || SUSPENDED_FALLBACK
    : generic;

/**
 * The sign-in screen, and the two steps a sign-in can pass through before it is one: the
 * authenticator code (two-factor), and — when the password typed was an emailed one-time
 * password — a forced password change.
 *
 * `notice` is one sentence shown above the form by whatever sent the person here, and
 * `noticeDetail` what that page wants said beneath it. ForgotPassword renders this
 * component in place with the server's one answer and how to use the one-time password;
 * AcceptInvite navigates to /login with "your account is active", which {@link LoginRoute}
 * picks up. The component itself adds nothing: the instructions for a one-time password
 * used to be hard-coded under ANY notice, and would have been said to someone who had just
 * chosen their own password.
 */
function Login({ notice = '', noticeDetail = '' }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAUsername, setTwoFAUsername] = useState('');

  // Forced password change, after signing in with a one-time password. The grant lives in
  // memory only — it is not a session, and a reload simply means signing in again.
  const [changeToken, setChangeToken] = useState('');
  const [changeUsername, setChangeUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  /** A sign-in that used a one-time password ends here rather than in the app. */
  const requirePasswordChange = (result) => {
    setNeeds2FA(false);
    setTempToken('');
    setTwoFACode('');
    setPassword('');
    setChangeToken(result.changeToken);
    setChangeUsername(result.username);
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      if (result.twoFactorRequired) {
        setTempToken(result.tempToken);
        setTwoFAUsername(result.username);
        setNeeds2FA(true);
        setLoading(false);
      } else if (result.passwordChangeRequired) {
        requirePasswordChange(result);
      } else {
        hardRedirect(landingFor(result));
      }
    } catch (err) {
      setError(messageFor(err, 'Invalid username or password'));
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // The response was previously discarded, which would have left a 2FA-enabled
      // admin on '/' no matter what the claim said.
      const result = await verify2fa(tempToken, twoFACode);
      if (result.passwordChangeRequired) {
        requirePasswordChange(result);
      } else {
        hardRedirect(landingFor(result));
      }
    } catch (err) {
      // A suspension can land inside the 2FA window; the backend honours that and
      // now maps it to 403 TENANT_SUSPENDED rather than a generic 401.
      setError(messageFor(err, 'Invalid verification code. Please try again.'));
      setLoading(false);
    }
  };

  const handleBack = () => {
    setNeeds2FA(false);
    setTempToken('');
    setTwoFACode('');
    setTwoFAUsername('');
    setChangeToken('');
    setChangeUsername('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setPassword('');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (newPassword !== confirmPassword) {
      // Checked before the request, so a typo costs nothing.
      setError('The two passwords do not match.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      // Saving the new password is what completes the sign-in.
      const result = await completePasswordReset(changeToken, newPassword);
      hardRedirect(landingFor(result));
    } catch (err) {
      const status = err?.response?.status;
      const reason = messageFor(err, serverMessage(err, 'Your password could not be changed. Try again.'));
      if (status === 404 || status === 410) {
        // The one-time password is spent or expired, or the sign-in timed out: there is
        // nothing left to finish here, so go back to the form that can start again.
        handleBack();
      }
      setError(reason);
      setLoading(false);
    }
  };

  // Forced password change
  if (changeToken) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h2>Veriform</h2>
          <h3>Choose a new password</h3>

          <p className="login-step-description">
            You signed in with a one-time password. Choose a new password
            for <strong>{changeUsername}</strong> to continue.
          </p>

          <form onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label htmlFor="newPassword">New password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm new password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                minLength={6}
                required
                disabled={loading}
              />
            </div>

            {error && <div className="error-message" role="alert">{error}</div>}

            <button type="submit" disabled={loading} className="btn btn-primary btn-lg btn-block">
              {loading ? 'Saving...' : 'Save password'}
            </button>
          </form>

          <button className="btn btn-ghost btn-sm btn-block twofa-back" onClick={handleBack} disabled={loading}>
            ← Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // 2FA Verification Screen
  if (needs2FA) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h2>Veriform</h2>
          <h3>Two-Factor Authentication</h3>

          <p className="twofa-login-description">
            Enter the 6-digit code from your authenticator app for <strong>{twoFAUsername}</strong>.
          </p>

          <form onSubmit={handle2FASubmit}>
            <div className="form-group">
              <label htmlFor="twoFACode">Verification Code</label>
              <input
                type="text"
                id="twoFACode"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="twofa-login-input"
                maxLength={6}
                autoComplete="one-time-code"
                required
                disabled={loading}
                autoFocus
              />
            </div>

            {error && <div className="error-message" role="alert">{error}</div>}

            <button
              type="submit"
              disabled={twoFACode.length !== 6 || loading}
              className="btn btn-primary btn-lg btn-block"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>

          <button className="btn btn-ghost btn-sm btn-block twofa-back" onClick={handleBack} disabled={loading}>
            ← Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Veriform</h2>
        <h3>Sign In</h3>

        {/* What the page that sent the person here has to say. For a forgotten-password
            request it is the same words whether or not an account matched, so nothing
            rendered here may depend on which it was. */}
        {notice && (
          <div className="login-notice" role="status">
            <p>{notice}</p>
            {noticeDetail && <p>{noticeDetail}</p>}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          {error && <div className="error-message" role="alert">{error}</div>}

          <button type="submit" disabled={loading} className="btn btn-primary btn-lg btn-block">
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* A plain link: this screen and the forgotten-password page render outside the app shell. */}
        <p className="login-forgot">
          <a href="/forgot-password">Forgot password?</a>
        </p>
      </div>
    </div>
  );
}

/**
 * The `/login` ROUTE: the sign-in form, with whatever notice the page that navigated here
 * left in router state.
 *
 * It exists because `navigate('/login', { state: { notice } })` — what AcceptInvite does once
 * somebody has chosen their password — put the message somewhere nothing read. `Login` takes
 * the notice as a prop so it can be rendered in place (ForgotPassword) and tested without a
 * router; this is the one place the two meet.
 *
 * Only a string is rendered: router state is whatever the navigating code put there.
 */
export function LoginRoute() {
  const location = useLocation();
  const notice = location.state?.notice;
  return <Login notice={typeof notice === 'string' ? notice : ''} />;
}

export default Login;
